import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FieldSeasonItem } from './FieldSeasonTable';

interface Props {
  // Open while set.
  item: FieldSeasonItem | null;
  authHeader: { Authorization: string };
  onClose: () => void;
  onSaved: () => void;
}

interface ChangeRow {
  effectiveDate: string; // yyyy-mm-dd
  irrigationTypeId: string;
}

const addDays = (isoDate: string, days: number) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// Admin editor for a field season's Irrigated / Dryland: the season value
// (from the season's start, and what labels the field season in reports)
// plus any change part way through — e.g. the water ran out and the crop
// finished dryland. Each change applies from its date until the next one or
// the season's end, and a record shows the value on its own date. Saved
// together: PUT api/field-seasons/{id}/irrigation.
const FieldSeasonIrrigationDialog: React.FC<Props> = ({ item, authHeader, onClose, onSaved }) => {
  const [types, setTypes] = useState<{ id: string; name: string }[]>([]);
  const [seasonValue, setSeasonValue] = useState('');
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/irrigation-types', { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then(setTypes)
      .catch(() => setTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!item) return;
    setSeasonValue(item.irrigationTypeId ?? '');
    setChanges(item.irrigationChanges.map((c) => ({ effectiveDate: c.effectiveDate, irrigationTypeId: c.irrigationTypeId })));
    setError(null);
  }, [item]);

  const updateChange = (index: number, patch: Partial<ChangeRow>) =>
    setChanges((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const save = async () => {
    if (!item) return;
    if (changes.some((c) => !c.effectiveDate || !c.irrigationTypeId)) {
      setError('Each change needs a date and a value.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/field-seasons/${item.id}/irrigation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ irrigationTypeId: seasonValue || null, changes }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Failed to save');
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // A change on the season's first day would just be the season value.
  const minDate = item ? addDays(item.timePeriodStartDate, 1) : undefined;
  const maxDate = item?.timePeriodEndDate;

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Irrigated / Dryland — {item?.farmName ? `${item.farmName} ` : ''}{item?.fieldName}, {item?.timePeriodName}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          select
          fullWidth
          size="small"
          label="Season value"
          value={seasonValue}
          onChange={(e) => setSeasonValue(e.target.value)}
          helperText="From the start of the season. This is what labels the field season in reports."
          sx={{ mt: 1 }}
        >
          <MenuItem value=""><em>Not set</em></MenuItem>
          {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>

        <Typography variant="subtitle2" sx={{ mt: 3 }}>Changes during the season</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Each applies from its date until the next change or the end of the season. Records show the value on their own date.
        </Typography>
        <Stack spacing={1.5}>
          {changes.map((change, index) => (
            <Box key={index} display="flex" gap={1} alignItems="center">
              <TextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate, max: maxDate }}
                value={change.effectiveDate}
                onChange={(e) => updateChange(index, { effectiveDate: e.target.value })}
              />
              <TextField
                select
                size="small"
                label="Becomes"
                value={change.irrigationTypeId}
                onChange={(e) => updateChange(index, { irrigationTypeId: e.target.value })}
                sx={{ minWidth: 160 }}
              >
                {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <Tooltip title="Remove change">
                <IconButton size="small" onClick={() => setChanges((prev) => prev.filter((_, i) => i !== index))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setChanges((prev) => [...prev, { effectiveDate: '', irrigationTypeId: '' }])}
          sx={{ mt: 1 }}
        >
          Add change
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldSeasonIrrigationDialog;
